import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ACTIVE_BOT_STATES } from '@apiary/shared'
import type { BotDto, UpdateBotRequest } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { ApiError } from '@/lib/api-error'
import { describeError } from '@/lib/problem-message'
import { fieldErrorsOf } from '../shared/problem'
import { useMcAccounts, useServers } from '@/hooks/queries'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { useDraft } from '@/ui/hooks/useDraft'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { SelectField, Switch, TextField } from '@/components/ui/Field'

/**
 * Правка бота после создания: имя, сервер, аккаунт, автозапуск.
 *
 * Почему сервер и аккаунт меняются здесь, а не только в визарде: перевести бота
 * на другой сервер или подменить аккаунт — обычная эксплуатация, а не повод
 * удалять бота и создавать заново (заново он потерял бы историю и настройки
 * поведений).
 *
 * Сервер и аккаунт у ЗАПУЩЕННОГО бота API менять отказывается (409
 * `BOT_ALREADY_IN_STATE`, bots.service §update): воркер уже вошёл на старый
 * сервер под старым аккаунтом. Поэтому оба поля здесь блокируются заранее и с
 * объяснением — узнавать об этом из ошибки после нажатия «Сохранить» плохо.
 * Имя и автозапуск при этом менять можно на ходу.
 *
 * Карточка бота (`BotScreen`) отдаёт сюда ЖИВОЙ объект из кэша, а он меняется на
 * каждое `bot.state` по WS — то есть каждые пару секунд у работающего бота.
 * Поэтому поля держит `useDraft`, а не `useEffect(() => setForm(bot), [bot])`:
 * прежний вариант затирал набранное имя в момент фонового обновления, и
 * переименовать бота на ходу было практически невозможно.
 */
export interface EditBotDialogProps {
  bot: BotDto | null
  onClose: () => void
}

/** Ровно те поля, что показывает форма: остальное в бота приходит с сервера. */
interface BotFormState {
  name: string
  serverId: string
  mcAccountId: string
  autoStart: boolean
}

function formOf(bot: BotDto | null): BotFormState {
  return {
    name: bot?.name ?? '',
    serverId: bot?.serverId ?? '',
    mcAccountId: bot?.mcAccountId ?? '',
    autoStart: bot?.autoStart ?? false
  }
}

export function EditBotDialog({ bot, onClose }: EditBotDialogProps): JSX.Element {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)
  const servers = useServers()
  const accounts = useMcAccounts()

  // Метка формы — id бота: открыли другого бота или закрыли окно, и черновик
  // начинается заново. Пока id тот же, фоновое обновление лишь дополняет поля,
  // которых человек не касался.
  const draft = useDraft(formOf(bot), bot?.id ?? null)
  const form = draft.value
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Ошибка прошлой попытки не должна встречать пользователя в новом окне.
  // Зависимость — id, а не сам бот: от смены ссылки на объект (WS-тик) ошибку
  // сбрасывать не за что.
  useEffect(() => {
    setError(null)
    setFieldErrors({})
  }, [bot?.id])

  const save = useMutation({
    mutationFn: (dto: UpdateBotRequest) => {
      if (bot === null) throw new Error('нет бота для правки')
      return api.bots.update(bot.id, dto)
    },
    onSuccess: updated => {
      void queryClient.invalidateQueries({ queryKey: qk.bots.root })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.summary })
      push({
        message: t('bots.edit.saved', { name: updated.name }),
        tone: 'success',
        timeoutMs: DEFAULT_TOAST_MS
      })
      onClose()
    },
    /**
     * Отказ сервера показываем ПОД полем, если сервер сказал, какое поле
     * виновато: «имя занято» общим баннером наверху заставляет искать, что
     * именно исправлять. 409 `BOT_ALREADY_IN_STATE` поля не называет, но
     * относится ровно к серверу и аккаунту — вешаем на них.
     */
    onError: failure => {
      const fields = fieldErrorsOf(failure)
      const detail = describeError(failure, t).detail
      if (failure instanceof ApiError && failure.code === 'BOT_ALREADY_IN_STATE') {
        setFieldErrors({ serverId: detail, mcAccountId: detail })
        setError(null)
        return
      }
      setFieldErrors(fields)
      setError(Object.keys(fields).length > 0 ? null : detail)
    }
  })

  /**
   * Условие ровно то же, что проверяет API: активное состояние ИЛИ желаемое
   * `RUNNING` (бот в перезапуске тоже считается запущенным).
   */
  const locked =
    bot !== null &&
    ((ACTIVE_BOT_STATES as readonly string[]).includes(bot.state) || bot.desiredState === 'RUNNING')

  return (
    <Dialog
      open={bot !== null}
      onClose={onClose}
      title={t('bots.edit.title', { name: bot?.name ?? '' })}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={form.name.trim() === ''}
            onClick={() => {
              if (bot === null) return
              setError(null)
              // Отправляем только изменённое: PATCH без разницы не создаёт
              // записи в аудите и не дёргает воркер понапрасну.
              const dto: UpdateBotRequest = {
                ...(form.name.trim() === bot.name ? {} : { name: form.name.trim() }),
                // У запущенного бота эти поля заблокированы, но подстрахуемся:
                // отправить их всё равно значило бы получить 409.
                ...(locked || form.serverId === bot.serverId ? {} : { serverId: form.serverId }),
                ...(locked || form.mcAccountId === bot.mcAccountId
                  ? {}
                  : { mcAccountId: form.mcAccountId }),
                ...(form.autoStart === bot.autoStart ? {} : { autoStart: form.autoStart })
              }
              if (Object.keys(dto).length === 0) {
                onClose()
                return
              }
              save.mutate(dto)
            }}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label={t('bots.edit.name')}
          hint={t('bots.edit.nameHint')}
          value={form.name}
          required
          autoFocus
          error={fieldErrors.name ?? null}
          onChange={event => draft.patch({ name: event.target.value })}
        />

        <SelectField
          label={t('bots.edit.server')}
          value={form.serverId}
          disabled={locked}
          hint={locked ? t('bots.edit.lockedHint') : t('bots.edit.serverHint')}
          error={fieldErrors.serverId ?? null}
          onChange={event => draft.patch({ serverId: event.target.value })}
        >
          {(servers.data?.items ?? []).map(server => (
            <option key={server.id} value={server.id}>
              {server.name} — {server.host}:{server.port}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={t('bots.edit.account')}
          value={form.mcAccountId}
          disabled={locked}
          {...(locked ? { hint: t('bots.edit.lockedHint') } : {})}
          error={fieldErrors.mcAccountId ?? null}
          onChange={event => draft.patch({ mcAccountId: event.target.value })}
        >
          {(accounts.data?.items ?? []).map(account => (
            <option key={account.id} value={account.id}>
              {account.label}
              {account.gamertag === null ? '' : ` — ${account.gamertag}`}
            </option>
          ))}
        </SelectField>

        <div className="flex flex-col gap-2">
          <Switch
            label={t('bots.edit.autoStart')}
            checked={form.autoStart}
            onChange={next => draft.patch({ autoStart: next })}
          />
          <p className="text-xs text-fg-muted">{t('bots.edit.autoStartHint')}</p>
        </div>

        {error === null ? null : (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  )
}
